import React, { useMemo, createContext } from 'react'

// api
import loadB2cApiEndpoints from '../api/b2c'
import loadCmsApiEndpoints from '../api/cms'
import loadBloomreachApiEndpoints from '../api/bloomreach'

// utils
import createApiClient, { createBloomreachApiClient } from './axiosClientConfig'
import { DEFAULT_SHOP_ID, DEFAULT_MAP_ID, DEFAULT_COUNTRY_CODE } from '../appDefaults'
import { PRODUCTION_ENVIRONMENT } from './enums'
import { setAppContext } from './getAppContext'

// types
import { ApiEndpoints, AppContextModel, ContextProps } from '../types/types'

type Props = ContextProps

export const AppContext = createContext<AppContextModel>({
	apiBrowser: {} as ApiEndpoints,
	assetsPath: '',
	googleMapsApiKey: '',
	googleMapId: DEFAULT_MAP_ID,
	userToken: undefined,
	locale: '',
	countryCode: DEFAULT_COUNTRY_CODE,
	shopId: DEFAULT_SHOP_ID,
	apiUrl: '',
	appVersion: '',
	isAuthorized: false,
	sentryOptions: {
		isEnabled: false,
		environment: '',
		dsn: ''
	},
	cms: {
		url: ''
	},
	bloomreach: {
		url: ''
	},
	userInfo: {},
	productionEnvironment: PRODUCTION_ENVIRONMENT.PRODUCTION
})

/* Create api client for requests outside of server -> on client browser. If you want use this request you need to add your fetch function to return statement. */
const createBrowserApiClient = (props: Props): ApiEndpoints => {
	const { locale, apiUrl, appVersion, userToken, cms, bloomreach } = props
	// create axios instance
	const client = createApiClient({ locale, apiUrl, appVersion, token: userToken })
	const cmsApiClient = createApiClient({ locale, apiUrl: cms.url, appVersion, token: userToken })

	const bloomreachApiClient = createBloomreachApiClient({ locale, apiUrl: bloomreach.url, token: userToken })

	return {
		b2c: {
			...loadB2cApiEndpoints(client)
		},
		cms: {
			...loadCmsApiEndpoints(cmsApiClient)
		},
		bloomreach: {
			...loadBloomreachApiEndpoints(bloomreachApiClient)
		}
	}
}

const AppProvider: React.FC<React.PropsWithChildren<Props>> = (props) => {
	const value = useMemo(
		() => ({
			apiBrowser: createBrowserApiClient(props),
			...props
		}),
		[props]
	)

	setAppContext(value)

	return <AppContext.Provider value={value}>{props.children}</AppContext.Provider>
}

export default AppProvider
